
import { Component, Vue } from "vue-property-decorator";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import vTabbedContent from "@/components/vTabbedContent.vue";
import { UserTypes } from "@/models/User/UserTypes";
import UserRepository from "@/utils/API/UserRepository";
import { mapState } from "vuex";
import {FETCH_USER_DETAILS, TRANSLATIONS_FETCH} from "@/store/ActionTypes";
import CustomersRepository from "@/utils/API/CustomersRepository";
import GeneralRepository from "@/utils/API/GeneralRepository";
import RestaurantProfile from "@/models/User/RestaurantProfile";
import Country from "@/models/Country";
import Industry from "@/models/Industry";
import ChangePassword from "@/models/User/ChangePassword";
import { NutrientSource } from "@/models/NutrientSource/NutrientSource";
import SubscriptionRepository from "@/utils/API/SubscriptionRepository";
import Language from "@/models/Language";

// const vSelect2 = () => import("@/components/vSelect2.vue");

interface ProfileState {
  restaurantProfile: RestaurantProfile;
  countries: Country[];
  industries: Industry[];
  changePassword: ChangePassword;
  languages: Language[];
  language: string;
  nutrientSources: NutrientSource[];
}

@Component({
  components: {
    vBreadcrumbs,
    vTabbedContent,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountStoreState: (state: any) => state.AccountModule,
    }),
  },
})
class Profile extends Vue {
  localizationState: any;
  accountStoreState: any;

  profileState: ProfileState = {
    restaurantProfile: {
      defaultNutrientSource: null,
      phone: "",
      email: "",
      postalCode: "",
      province: "",
      city: "",
      country: null,
      address: "",
      name: "",
      id: 0,
      firstName: "",
      lastName: "",
      industry: null,
    },
    countries: [],
    industries: [],
    changePassword: {
      oldPassword: "",
      newPassword: "",
      repeatNewPassword: "",
    },
    languages: [],
    language: "",
    nutrientSources: [],
  };
  invalidPhone: boolean = false;
  phoneTouched: boolean = false;

  get tabs() {
    return [
      { id: 1, name: this.translations.get("profile_tab_personalinfo") },
      { id: 2, name: this.translations.get("profile_tab_password") },
      // NOTE: Spanish has no actual localization keys, thus we only have 1 language.
      // It doesn't make sense to show this option
      { id: 3, name: this.translations.get("profile_tab_language_set"), predicate: () => false },
    ];
  }

  validatePhone(response: { isValid: boolean }) {
    this.invalidPhone = !response.isValid;
  }

  submitRestaurantInfo() {
    this.$validator.validateAll("personalInfo").then((result) => {
      if (result && !this.invalidPhone) {
        // @ts-ignore
        this.$snotify.async(
          this.translations.get("profile_toastr_restaurantinfo_updating"),
          () =>
            new Promise((resolve, reject) => {
              CustomersRepository.updateRestaurantAccountProfile({
                defaultNutrientSource:
                  this.profileState.restaurantProfile.defaultNutrientSource,
                phone: this.profileState.restaurantProfile.phone,
                postalCode: this.profileState.restaurantProfile.postalCode,
                province: this.profileState.restaurantProfile.province,
                city: this.profileState.restaurantProfile.city,
                countryId: this.profileState.restaurantProfile.country
                  ? this.profileState.restaurantProfile.country.id
                  : null,
                industryId: this.profileState.restaurantProfile.industry
                  ? this.profileState.restaurantProfile.industry.id
                  : null,
                address: this.profileState.restaurantProfile.address,
                name: this.profileState.restaurantProfile.name,
                id: this.profileState.restaurantProfile.id,
                firstName: this.profileState.restaurantProfile.firstName,
                lastName: this.profileState.restaurantProfile.lastName,
                email: this.profileState.restaurantProfile.email,
              })
                .then(() => {
                  resolve({
                    body: this.translations.get(
                      "profile_toastr_personalinfo_updated"
                    ),
                  });
                })
                .catch((error) => {
                  let errorMessage = "";
                  if (error.response && error.response.data) {
                    errorMessage =
                      error.response.data.errorMessages[0].userVisibleMessage;
                  }
                  else {
                    errorMessage = this.translations.get(
                      "toastr_network_error"
                    );
                  }
                  reject({
                    body: errorMessage,
                  });
                });
            }),
          {
            timeout: 3500,
            closeOnClick: true,
          }
        );
      }
      else
        this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  submitChangePassword() {
    this.$validator.validateAll("changePassword").then((result) => {
      if (result) {
        // @ts-ignore
        this.$snotify.async(
          this.translations.get("profile_toastr_password_updating"),
          () =>
            new Promise((resolve, reject) => {
              UserRepository.changePassword({
                oldPassword: this.profileState.changePassword.oldPassword,
                newPassword: this.profileState.changePassword.newPassword,
                repeatNewPassword:
                  this.profileState.changePassword.repeatNewPassword,
              })
                .then(() => {
                  resolve({
                    body: this.translations.get(
                      "profile_toastr_password_updated"
                    ),
                  });
                })
                .catch((error) => {
                  let message = "";
                  if (error.response) {
                    message =
                      error.response.data.errorMessages[0].userVisibleMessage;
                  }
                  else {
                    message = this.translations.get("network_error");
                  }
                  reject({ body: message });
                });
            }),
          {
            timeout: 3500,
            closeOnClick: true,
          }
        );
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  updateLanguage(language: string) {
    // @ts-ignore
    this.$snotify.async(
      this.translations.get("profile_language_updating"),
      () =>
        new Promise((resolve, reject) => {
          UserRepository.updateLanguage({
            language: language,
          })
            .then(() => {
              this.$store
                .dispatch(FETCH_USER_DETAILS)
                .then((response) => {
                  let language: string = response.data.result.localization;
                  language = language.substring(0, 2);

                  if (this.$validator.dictionary.hasLocale(language)) {
                    this.$validator.localize(language);
                  }
                  else {
                    import(`vee-validate/dist/locale/${language}`).then(
                      (locale) => {
                        this.$validator.localize(language, locale);
                      }
                    );
                  }
                })
                .then(() => {
                  this.$store.dispatch(TRANSLATIONS_FETCH, {
                    category: "pages",
                    collections: [
                      "common",
                      "profile",
                      "customers",
                      "recipes",
                      "ingredients",
                      "managesubscription",
                    ],
                  });
                });
              resolve({
                body: this.translations.get("profile_toastr_language_updated"),
              });
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3500,
        closeOnClick: true,
      }
    );
  }
  get isOwner() {
    return this.$store.getters.getUserType === UserTypes.Owner;
  }

  get translations() {
    return this.localizationState.translations;
  }

  created() {
    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: ["common", "profile", "customers"],
      })
      .then(() => {
        document.title =
          this.translations.get("profile_title") +
          " " +
          this.translations.get("meta_title_postfix");
      });

    CustomersRepository.getRestaurantProfile()
      .then((response) => {
        this.profileState.restaurantProfile = response.data.result;
      })
      .catch((error) => {
        if (error.response)
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else this.$snotify.error(this.translations.get("network-error"));
      });

    GeneralRepository.listCountries()
      .then((response) => {
        this.profileState.countries = response.data.result;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.statusText
            //error.response.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    GeneralRepository.listIndustries()
      .then((response) => {
        this.profileState.industries = response.data.result;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.statusText
            //error.response.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    SubscriptionRepository.listCurrentSubscriptionNutrientSources().then(
      (response) => {
        this.profileState.nutrientSources = response.data.result;
      }
    );

    GeneralRepository.listLanguages()
      .then((response) => {
        this.profileState.languages = response.data.result;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.statusText
            //error.response.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }
}

export default Profile;
